import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { toast } from 'react-toastify';
import { uploadFileToBlob } from '../api/services/storageService';
import { Layout } from '../components/Layout';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useRole } from '../hooks/useRole';
import { emailTemplateService, emailAssetService } from '../api';
import { Button, ThemedText } from '../components';
import { extractItems } from '../utils/responseUtils';
import { EmailAssetPickerModal } from '../components/EmailAssetPickerModal';
import Handlebars from 'handlebars';
import { PageContainer, PageHeaderRow as PageHeader } from '../components/PageStyles';
import { TableContainer, Table, Thead, Th, Tbody, Tr, Td } from '../components/TableStyles';
import { ModalOverlay as Overlay, ModalHeader } from '../components/ModalStyles';

// --- Styled Components ---

const TabRow = styled.div`
  display: flex;
  gap: ${p => p.theme.spacing.sm};
  margin-bottom: ${p => p.theme.spacing.lg};
  border-bottom: 2px solid ${p => p.theme.colors.border};
`;

const Tab = styled.button`
  padding: ${p => p.theme.spacing.sm} ${p => p.theme.spacing.lg};
  background: none;
  border: none;
  border-bottom: 2px solid ${p => p.active ? p.theme.colors.tint : 'transparent'};
  color: ${p => p.active ? p.theme.colors.tint : p.theme.colors.muted};
  font-weight: ${p => p.active ? '600' : '400'};
  cursor: pointer;
  margin-bottom: -2px;
  font-size: 14px;
  transition: all 0.2s ease;

  &:hover {
    color: ${p => p.theme.colors.tint};
  }
`;

const CategorySection = styled.div`
  margin-bottom: ${p => p.theme.spacing.xl};
`;

const CategoryTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${p => p.theme.colors.text};
  margin-bottom: ${p => p.theme.spacing.md};
  padding-left: ${p => p.theme.spacing.sm};
`;

const Badge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  background-color: ${p => p.bg || '#22c55e20'};
  color: ${p => p.color || '#22c55e'};
`;

const ToggleSwitch = styled.button`
  width: 44px;
  height: 24px;
  border-radius: 12px;
  border: none;
  cursor: pointer;
  position: relative;
  transition: background-color 0.2s ease;
  background-color: ${p => p.enabled ? p.theme.colors.success || '#22c55e' : p.theme.colors.border};

  &::after {
    content: '';
    position: absolute;
    top: 2px;
    left: ${p => p.enabled ? '22px' : '2px'};
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: white;
    transition: left 0.2s ease;
    box-shadow: 0 1px 3px rgba(0,0,0,0.2);
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${p => p.theme.spacing.sm};
  flex-wrap: wrap;
`;

const SmallButton = styled.button`
  padding: 4px 10px;
  border-radius: ${p => p.theme.borderRadius.sm};
  border: 1px solid ${p => p.theme.colors.border};
  background: ${p => p.variant === 'primary' ? p.theme.colors.tint : p.variant === 'success' ? (p.theme.colors.success || '#22c55e') : 'transparent'};
  color: ${p => (p.variant === 'primary' || p.variant === 'success') ? 'white' : p.theme.colors.text};
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    opacity: 0.8;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const LoadingState = styled.div`
  text-align: center;
  padding: ${p => p.theme.spacing.xl};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${p => p.theme.spacing.xl};
  color: ${p => p.theme.colors.muted};
`;

// --- Modal ---

const ModalContent = styled.div`
  background-color: ${p => p.theme.colors.background};
  border-radius: ${p => p.theme.borderRadius.lg};
  padding: ${p => p.theme.spacing.xl};
  max-width: ${p => p.wide ? '700px' : '500px'};
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: ${p => p.theme.shadows?.xl || '0 25px 50px rgba(0,0,0,0.25)'};
`;

const FormGroup = styled.div`
  margin-bottom: ${p => p.theme.spacing.md};
`;

const Label = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: ${p => p.theme.colors.text};
  margin-bottom: 4px;
`;

const TextInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.sm};
  background-color: ${p => p.theme.colors.backgroundSecondary};
  color: ${p => p.theme.colors.text};
  font-size: 14px;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${p => p.theme.colors.tint};
  }
`;

const DescriptionText = styled.p`
  font-size: 12px;
  color: ${p => p.theme.colors.muted};
  margin-top: 2px;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${p => p.theme.spacing.sm};
  margin-top: ${p => p.theme.spacing.lg};
`;

// --- SES Name display ---

const SesName = styled.span`
  font-size: 11px;
  color: ${p => p.theme.colors.muted};
  display: block;
  margin-top: 2px;
  font-family: monospace;
`;

// --- SES Status ---

const StatusIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: ${p => p.color || p.theme.colors.text};

  &::before {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: ${p => p.dotColor || '#22c55e'};
    flex-shrink: 0;
  }
`;

const VariableList = styled.span`
  font-family: monospace;
  font-size: 11px;
  color: ${p => p.theme.colors.muted};
  word-break: break-all;
`;

// --- Preview ---

const PreviewFrame = styled.iframe`
  width: 100%;
  height: 500px;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.sm};
  background: white;
`;

// --- Log Table ---

const LogStatusBadge = styled.span`
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  background-color: ${p => p.status === 'Sent' ? '#22c55e20' : '#ef444420'};
  color: ${p => p.status === 'Sent' ? '#22c55e' : '#ef4444'};
`;

const PaginationRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${p => p.theme.spacing.md};
  border-top: 1px solid ${p => p.theme.colors.border};
  font-size: 13px;
  color: ${p => p.theme.colors.muted};
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 60px;
  padding: 8px 12px;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.sm};
  background-color: ${p => p.theme.colors.backgroundSecondary};
  color: ${p => p.theme.colors.text};
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  box-sizing: border-box;
  &:focus { outline: none; border-color: ${p => p.theme.colors.tint}; }
`;

const ColorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ColorSwatch = styled.input`
  width: 40px;
  height: 36px;
  padding: 2px;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.sm};
  background: transparent;
  cursor: pointer;
`;

const BrandingGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${p => p.theme.spacing.md};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const BrandingCard = styled.div`
  background-color: ${p => p.theme.colors.backgroundSecondary};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.lg};
  padding: ${p => p.theme.spacing.lg};
`;

const BrandingCardTitle = styled.h4`
  font-size: 14px;
  font-weight: 600;
  color: ${p => p.theme.colors.text};
  margin-bottom: ${p => p.theme.spacing.md};
  margin-top: 0;
`;

const RefTextArea = styled.textarea`
  width: 100%;
  height: 300px;
  padding: 12px;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.sm};
  background-color: ${p => p.theme.colors.backgroundSecondary};
  color: ${p => p.theme.colors.text};
  font-size: 12px;
  font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
  resize: vertical;
  box-sizing: border-box;
  line-height: 1.5;
  tab-size: 2;
  &:focus { outline: none; border-color: ${p => p.theme.colors.tint}; }
`;

// --- Assets Tab ---

const AssetGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: ${p => p.theme.spacing.md};
`;

const AssetCard = styled.div`
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  overflow: hidden;
  background-color: ${p => p.theme.colors.backgroundSecondary};
  transition: all 0.2s ease;
  &:hover { border-color: ${p => p.theme.colors.tint}; }
`;

const AssetThumb = styled.div`
  width: 100%;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${p => p.theme.colors.background};
  overflow: hidden;
  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
`;

const AssetCardBody = styled.div`
  padding: ${p => p.theme.spacing.sm} ${p => p.theme.spacing.md};
`;

const AssetFileName = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: ${p => p.theme.colors.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const AssetMeta = styled.div`
  font-size: 11px;
  color: ${p => p.theme.colors.muted};
  margin-top: 2px;
`;

const AssetActions = styled.div`
  display: flex;
  gap: 4px;
  margin-top: ${p => p.theme.spacing.sm};
`;

const LogoRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.sm};
`;

const LogoPreview = styled.img`
  max-height: 40px;
  max-width: 120px;
  object-fit: contain;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.sm};
  padding: 4px;
  background: white;
`;

const UploadArea = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.md};
  margin-bottom: ${p => p.theme.spacing.lg};
`;

const HiddenInput = styled.input`
  display: none;
`;

const ProgressBarSmall = styled.div`
  width: 200px;
  height: 4px;
  background: ${p => p.theme.colors.border};
  border-radius: 2px;
  overflow: hidden;
`;

const ProgressFillSmall = styled.div`
  height: 100%;
  background: ${p => p.theme.colors.tint};
  width: ${p => p.$progress}%;
  transition: width 0.3s ease;
`;

const RefPreviewFrame = styled.iframe`
  width: 100%;
  height: 300px;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.sm};
  background: white;
`;

// --- Batch Regenerate Modal Styles ---

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const Spinner = styled.span`
  display: inline-block;
  animation: ${spin} 1s linear infinite;
`;

const ModeCardGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${p => p.theme.spacing.md};
  margin-bottom: ${p => p.theme.spacing.lg};
`;

const ModeCard = styled.label`
  display: block;
  padding: ${p => p.theme.spacing.md};
  border: 2px solid ${p => p.$selected ? p.theme.colors.tint : p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  background: ${p => p.$selected ? `${p.theme.colors.tint}10` : p.theme.colors.backgroundSecondary};
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: ${p => p.theme.colors.tint};
  }
`;

const ModeCardTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${p => p.theme.colors.text};
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ModeCardDesc = styled.div`
  font-size: 12px;
  color: ${p => p.theme.colors.muted};
  line-height: 1.4;
`;

const TemplateSectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${p => p.theme.spacing.sm};
`;

const TemplateCategoryLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: ${p => p.theme.colors.muted};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: ${p => p.theme.spacing.sm} 0 4px;
  border-bottom: 1px solid ${p => p.theme.colors.border};
  margin-bottom: 4px;
`;

const TemplateCheckRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 4px;
  cursor: pointer;
  border-radius: 4px;
  font-size: 13px;
  color: ${p => p.theme.colors.text};
  transition: background 0.1s;

  &:hover {
    background: ${p => p.theme.colors.backgroundSecondary};
  }
`;

const ProgressRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 0;
  border-bottom: 1px solid ${p => p.theme.colors.border};
  font-size: 13px;
`;

const ProgressRowName = styled.div`
  flex: 1;
  color: ${p => p.theme.colors.text};
  font-weight: 500;
`;

const ProgressRowCategory = styled.span`
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 8px;
  background: ${p => p.theme.colors.backgroundSecondary};
  color: ${p => p.theme.colors.muted};
`;

const ProgressRowStatus = styled.div`
  width: 80px;
  text-align: right;
  font-size: 12px;
  color: ${p =>
    p.$status === 'complete' ? '#22c55e' :
    p.$status === 'error' ? '#ef4444' :
    p.$status === 'processing' ? p.theme.colors.tint :
    p.theme.colors.muted};
`;

const SummaryBox = styled.div`
  text-align: center;
  padding: ${p => p.theme.spacing.xl};
`;

const SummaryTitle = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: ${p => p.theme.colors.text};
  margin-bottom: ${p => p.theme.spacing.sm};
`;

const SummaryLine = styled.div`
  font-size: 14px;
  color: ${p => p.$error ? '#ef4444' : '#22c55e'};
  margin-top: 6px;
`;

const ErrorList = styled.div`
  margin-top: ${p => p.theme.spacing.md};
  text-align: left;
  max-height: 200px;
  overflow-y: auto;
  font-size: 12px;
  color: ${p => p.theme.colors.muted};
`;

const SelectAllLink = styled.button`
  background: none;
  border: none;
  color: ${p => p.theme.colors.tint};
  font-size: 12px;
  cursor: pointer;
  padding: 0;
  text-decoration: underline;
`;

// --- Component ---

export const EmailTemplatesPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isSuperAdmin } = useRole();
  const [activeTab, setActiveTab] = useState('templates');
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editModal, setEditModal] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  // Create SES template state
  const [creating, setCreating] = useState({});

  // Preview modal
  const [previewModal, setPreviewModal] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Logs state
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logPage, setLogPage] = useState(1);
  const [logTotalPages, setLogTotalPages] = useState(1);
  const [logTotalCount, setLogTotalCount] = useState(0);

  // Send Test modal state
  const [sendTestModal, setSendTestModal] = useState(null);
  const [sendTestForm, setSendTestForm] = useState({ email: '', name: '', params: {} });
  const [sendingTest, setSendingTest] = useState(false);
  const [sendTestLoading, setSendTestLoading] = useState(false);

  // Assets state
  const [assets, setAssets] = useState([]);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [assetUploading, setAssetUploading] = useState(false);
  const [assetUploadProgress, setAssetUploadProgress] = useState(0);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Branding logo picker
  const [logoPickerOpen, setLogoPickerOpen] = useState(false);

  // Branding state
  const [brandingConfig, setBrandingConfig] = useState(null);
  const [brandingLoading, setBrandingLoading] = useState(false);
  const [brandingSaving, setBrandingSaving] = useState(false);
  const [generatingRefHtml, setGeneratingRefHtml] = useState(false);
  const [brandingForm, setBrandingForm] = useState({
    companyName: 'aQRate',
    logoUrl: '',
    primaryColor: '#2563eb',
    secondaryColor: '',
    styleDescription: '',
    referenceTemplateHtml: '',
  });

  // Batch regenerate state
  const [batchModal, setBatchModal] = useState(false);
  const [batchMode, setBatchMode] = useState('Redesign');
  const [batchTier, setBatchTier] = useState(2); // 1=Fast, 2=Balanced, 3=Powerful
  const [batchParallel, setBatchParallel] = useState(false);
  const [batchSelected, setBatchSelected] = useState(null);
  const [batchStep, setBatchStep] = useState('configure'); // 'configure' | 'progress' | 'summary'
  const [batchProgress, setBatchProgress] = useState({}); // templateId -> { status, error, tokens }
  const [batchSummary, setBatchSummary] = useState(null);
  const [batchAbortController, setBatchAbortController] = useState(null);
  // Use a ref to always have latest progress for the cancel summary (avoids stale closure)
  const batchProgressRef = React.useRef({});

  const openBatchModal = () => {
    const allIds = templates.map(t => t.id);
    setBatchSelected(new Set(allIds));
    setBatchMode('Redesign');
    setBatchTier(2);
    setBatchParallel(false);
    setBatchStep('configure');
    setBatchProgress({});
    batchProgressRef.current = {};
    setBatchSummary(null);
    setBatchModal(true);
  };

  const closeBatchModal = () => {
    if (batchAbortController) {
      batchAbortController.abort();
      setBatchAbortController(null);
    }
    setBatchModal(false);
  };

  const handleBatchStart = async () => {
    if (!batchSelected || batchSelected.size === 0) {
      toast.warn('Select at least one template');
      return;
    }

    const abortController = new AbortController();
    setBatchAbortController(abortController);
    setBatchStep('progress');
    setBatchProgress({});
    batchProgressRef.current = {};
    setBatchSummary(null);

    let doneCalled = false;
    try {
      await emailTemplateService.batchRegenerateStream(
        { mode: batchMode, tier: batchTier, runParallel: batchParallel, templateIds: Array.from(batchSelected) },
        {
          onProgress: (event) => {
            const entry = { status: event.status, error: event.error, tokens: event.tokens };
            batchProgressRef.current = { ...batchProgressRef.current, [event.templateId]: entry };
            setBatchProgress(prev => ({ ...prev, [event.templateId]: entry }));
          },
          onDone: (summary) => {
            doneCalled = true;
            // Delay transition so the last complete event's progress update renders first
            // (React 18 automatic batching can merge the last progress + done into one render)
            setTimeout(() => {
              setBatchSummary(summary);
              setBatchStep('summary');
              setBatchAbortController(null);
            }, 100);
          },
        },
        abortController.signal,
      );
      // Fallback: if stream ended without a 'done' event (e.g. SSE parse error), build summary from progress
      if (!doneCalled) {
        const latest = batchProgressRef.current;
        const succeeded = Object.values(latest).filter(p => p.status === 'complete').length;
        const failed = Object.values(latest).filter(p => p.status === 'error').length;
        setBatchSummary({ total: succeeded + failed, succeeded, failed });
        setBatchStep('summary');
        setBatchAbortController(null);
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        // Use ref — always has the latest progress, no stale closure
        const latest = batchProgressRef.current;
        const succeeded = Object.values(latest).filter(p => p.status === 'complete').length;
        const failed = Object.values(latest).filter(p => p.status === 'error').length;
        setBatchSummary({ total: succeeded + failed, succeeded, failed, cancelled: true });
        setBatchStep('summary');
      } else {
        toast.error(err.message || 'Batch regeneration failed');
        setBatchStep('configure');
      }
      setBatchAbortController(null);
    }
  };

  useEffect(() => {
    loadTemplates();
  }, []);

  useEffect(() => {
    if (activeTab === 'logs') {
      loadLogs();
    }
    if (activeTab === 'branding' && !brandingConfig) {
      loadBrandingConfig();
    }
    if (activeTab === 'assets') {
      loadAssets();
    }
  }, [activeTab, logPage]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const response = await emailTemplateService.getTemplates();
      const items = extractItems(response);
      setTemplates(items);
    } catch (err) {
      toast.error('Failed to load email templates');
    } finally {
      setLoading(false);
    }
  };

  const loadLogs = async () => {
    try {
      setLogsLoading(true);
      const response = await emailTemplateService.getLogs({ page: logPage, pageSize: 20 });
      const data = response?.data || response;
      setLogs(data.items || []);
      setLogTotalPages(data.totalPages || 1);
      setLogTotalCount(data.totalCount || 0);
    } catch (err) {
      toast.error('Failed to load email logs');
    } finally {
      setLogsLoading(false);
    }
  };

  const openEditModal = (template) => {
    setEditModal(template);
    setEditForm({
      senderName: template.senderName || '',
      senderEmail: template.senderEmail || '',
    });
  };

  const handleSave = async () => {
    if (!editModal) return;
    setSaving(true);

    try {
      const payload = {
        senderName: editForm.senderName || null,
        senderEmail: editForm.senderEmail || null,
      };

      const response = await emailTemplateService.updateTemplate(editModal.id, payload);
      const updated = response?.data || response;

      setTemplates(prev => prev.map(t => t.id === editModal.id ? { ...t, ...updated } : t));
      setEditModal(null);
      toast.success('Template updated');
    } catch (err) {
      toast.error('Failed to update template');
    } finally {
      setSaving(false);
    }
  };

  const openSendTestModal = async (template) => {
    setSendTestLoading(true);
    setSendTestModal(template);
    setSendTestForm({
      email: user?.email || '',
      name: user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : 'Test User',
      params: {},
    });

    try {
      const response = await emailTemplateService.getVariableInfo(template.id);
      const data = response?.data || response;
      const variables = data.variables || data || [];

      const builtinDefaults = {
        firstName: user?.firstName || 'Test',
        lastName: user?.lastName || 'User',
        resetLink: 'https://portal.aqrate.ca/reset-password?token=test-token-123',
        organizationName: 'Test Organization',
        formName: 'Test Form',
        submissionId: '12345',
        workOrderNumber: 'WO-001',
        expiryDate: new Date(Date.now() + 7 * 86400000).toISOString().split('T')[0],
        title: 'Test Work Order',
        assignedByName: 'Admin User',
        planName: 'Professional',
        daysRemaining: '7',
        verificationLink: 'https://portal.aqrate.ca/verify?token=test',
        reportDownloadLink: 'https://api.aqrate.ca/r/sample-token',
        reportName: 'Sample Report.pdf',
        errorMessage: 'Test error message',
      };

      const defaultParams = {};
      const varList = Array.isArray(variables) ? variables : [];
      varList.forEach(v => {
        const name = typeof v === 'string' ? v : v.name;
        const defVal = typeof v === 'object' ? v.defaultValue : undefined;
        defaultParams[name] = defVal || builtinDefaults[name] || `test_${name}`;
      });

      setSendTestForm(prev => ({ ...prev, params: defaultParams }));
    } catch (err) {
      // If variable info fails, continue with empty params
      toast.warn('Could not load template variables');
    } finally {
      setSendTestLoading(false);
    }
  };

  const handleSendTest = async () => {
    if (!sendTestModal) return;
    setSendingTest(true);
    try {
      const result = await emailTemplateService.sendTestEmail(sendTestModal.id, {
        recipientEmail: sendTestForm.email,
        recipientName: sendTestForm.name,
        templateParams: Object.keys(sendTestForm.params).length > 0 ? sendTestForm.params : undefined,
      });
      const data = result?.data || result;
      if (data.success) {
        toast.success('Test email sent! Check your inbox.');
        setSendTestModal(null);
      } else {
        toast.error(data.message || 'Failed to send test email');
      }
    } catch (err) {
      const message = err?.response?.data?.message || 'Failed to send test email';
      toast.error(message);
    } finally {
      setSendingTest(false);
    }
  };

  const handleCreateSesTemplate = async (template) => {
    setCreating(prev => ({ ...prev, [template.id]: true }));
    try {
      const response = await emailTemplateService.createSesTemplate(template.id);
      const updated = response?.data || response;
      setTemplates(prev => prev.map(t => t.id === template.id ? { ...t, ...updated } : t));
      toast.success(`SES template created: ${updated.sesTemplateName}`);
    } catch (err) {
      const message = err?.response?.data?.message || 'Failed to create SES template';
      toast.error(message);
    } finally {
      setCreating(prev => ({ ...prev, [template.id]: false }));
    }
  };

  const handlePreview = async (template) => {
    setPreviewLoading(true);
    try {
      const [contentResp, varResp] = await Promise.all([
        emailTemplateService.getSesContent(template.id),
        emailTemplateService.getVariableInfo(template.id).catch(() => null),
      ]);
      const data = contentResp?.data || contentResp;
      if (data?.htmlContent) {
        let rendered = data.htmlContent;
        try {
          const varData = varResp?.data || varResp;
          const sampleData = {};
          if (varData) {
            [...(varData.eventVariables || []), ...(varData.globalVariables || [])].forEach(v => {
              sampleData[v.name] = v.defaultValue || v.name;
            });
          }
          rendered = Handlebars.compile(data.htmlContent, { noEscape: true })(sampleData);
        } catch { /* show raw HTML if compilation fails */ }
        setPreviewModal({ name: template.name, html: rendered });
      } else {
        toast.error('No HTML content available for preview');
      }
    } catch {
      toast.error('Failed to load template preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const loadBrandingConfig = async () => {
    setBrandingLoading(true);
    try {
      const response = await emailTemplateService.getBrandingConfig();
      const data = response?.data || response;
      setBrandingConfig(data);
      setBrandingForm({
        companyName: data.companyName || 'aQRate',
        logoUrl: data.logoUrl || '',
        primaryColor: data.primaryColor || '#2563eb',
        secondaryColor: data.secondaryColor || '',
        styleDescription: data.styleDescription || '',
        referenceTemplateHtml: data.referenceTemplateHtml || '',
      });
    } catch (err) {
      toast.error('Failed to load branding config');
    } finally {
      setBrandingLoading(false);
    }
  };

  const handleBrandingSave = async () => {
    setBrandingSaving(true);
    try {
      const response = await emailTemplateService.updateBrandingConfig({
        companyName: brandingForm.companyName,
        logoUrl: brandingForm.logoUrl || null,
        primaryColor: brandingForm.primaryColor,
        secondaryColor: brandingForm.secondaryColor || null,
        styleDescription: brandingForm.styleDescription || null,
        referenceTemplateHtml: brandingForm.referenceTemplateHtml || null,
      });
      const data = response?.data || response;
      setBrandingConfig(data);
      toast.success('Branding config saved');
    } catch (err) {
      toast.error('Failed to save branding config');
    } finally {
      setBrandingSaving(false);
    }
  };

  const loadAssets = async () => {
    setAssetsLoading(true);
    try {
      const response = await emailAssetService.getAll();
      const items = response?.data || response;
      setAssets(Array.isArray(items) ? items : []);
    } catch {
      toast.error('Failed to load email assets');
    } finally {
      setAssetsLoading(false);
    }
  };

  const handleAssetUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    const allowed = ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!allowed.includes(file.type)) {
      toast.error('Only image files (PNG, JPEG, GIF, WebP, SVG) are allowed');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be under 5 MB');
      return;
    }

    setAssetUploading(true);
    setAssetUploadProgress(0);
    try {
      const { blobPath: tempPath } = await uploadFileToBlob(file, (loaded, total, pct) => {
        setAssetUploadProgress(pct);
      });

      const regResp = await emailAssetService.register({
        fileName: file.name,
        tempBlobPath: tempPath,
        contentType: file.type,
        fileSize: file.size,
      });
      const newAsset = regResp?.data || regResp;
      setAssets(prev => [newAsset, ...prev]);
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(err?.message || 'Upload failed');
    } finally {
      setAssetUploading(false);
      setAssetUploadProgress(0);
    }
  };

  const handleAssetDelete = async (id) => {
    try {
      await emailAssetService.delete(id);
      setAssets(prev => prev.filter(a => a.id !== id));
      setDeleteConfirm(null);
      toast.success('Asset deleted');
    } catch {
      toast.error('Failed to delete asset');
    }
  };

  const copyAssetUrl = (url) => {
    navigator.clipboard.writeText(url).then(() => {
      toast.success('URL copied to clipboard');
    }).catch(() => {
      toast.error('Failed to copy URL');
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Group templates by category
  const grouped = templates.reduce((acc, t) => {
    const cat = t.category || 'Other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(t);
    return acc;
  }, {});

  const categoryOrder = ['Authentication', 'Submissions', 'WorkOrders', 'Reports', 'LabReports', 'Tasks', 'System'];
  const sortedCategories = categoryOrder.filter(c => grouped[c]);
  const extraCategories = Object.keys(grouped).filter(c => !categoryOrder.includes(c));
  const allCategories = [...sortedCategories, ...extraCategories];

  return (
    <Layout>
      <PageContainer>
        <PageHeader theme={theme}>
          <ThemedText type="title" style={{ fontSize: '24px' }}>
            Email Templates
          </ThemedText>
        </PageHeader>

        <TabRow theme={theme}>
          <Tab theme={theme} active={activeTab === 'templates'} onClick={() => setActiveTab('templates')}>
            Templates
          </Tab>
          <Tab theme={theme} active={activeTab === 'logs'} onClick={() => setActiveTab('logs')}>
            Email Logs
          </Tab>
          <Tab theme={theme} active={activeTab === 'branding'} onClick={() => setActiveTab('branding')}>
            Branding
          </Tab>
          <Tab theme={theme} active={activeTab === 'assets'} onClick={() => setActiveTab('assets')}>
            Assets
          </Tab>
        </TabRow>

        {activeTab === 'templates' && (
          <>
            <div style={{
              padding: '10px 16px',
              marginBottom: 16,
              background: theme.colors.surfaceAlt || theme.colors.surface,
              border: `1px solid ${theme.colors.border}`,
              borderRadius: 8,
              fontSize: 13,
              color: theme.colors.textSecondary,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}>
              Notification delivery is now managed in{' '}
              <span
                style={{ color: theme.colors.primary, cursor: 'pointer', fontWeight: 500 }}
                onClick={() => navigate('/notification-events')}
              >
                Notification Events
              </span>
              . This page is for editing template content only.
            </div>
            {loading ? (
              <LoadingState theme={theme}>Loading templates...</LoadingState>
            ) : templates.length === 0 ? (
              <EmptyState theme={theme}>No email templates found. Run the seeder to create default templates.</EmptyState>
            ) : (
              allCategories.map(category => (
                <CategorySection key={category} theme={theme}>
                  <CategoryTitle theme={theme}>{category}</CategoryTitle>
                  <TableContainer theme={theme}>
                    <Table>
                      <Thead theme={theme}>
                        <tr>
                          <Th theme={theme}>Name</Th>
                          <Th theme={theme}>Event Type</Th>
                          <Th theme={theme}>SES Status</Th>
                          <Th theme={theme}>Actions</Th>
                        </tr>
                      </Thead>
                      <Tbody>
                        {grouped[category].map(t => {
                          const isCreating = creating[t.id];

                          return (
                            <Tr key={t.id} theme={theme}>
                              <Td theme={theme}>
                                <div style={{ fontWeight: 500 }}>
                                  {t.name}
                                </div>
                                {t.sesTemplateName && (
                                  <SesName theme={theme}>{t.sesTemplateName}</SesName>
                                )}
                                <DescriptionText theme={theme}>{t.description}</DescriptionText>
                              </Td>
                              <Td theme={theme}>
                                <Badge theme={theme} bg={`${theme.colors.tint}20`} color={theme.colors.tint}>
                                  {t.eventType}
                                </Badge>
                              </Td>
                              <Td theme={theme}>
                                {t.sesTemplateExists ? (
                                  <>
                                    <StatusIndicator theme={theme} dotColor="#22c55e" color="#22c55e">
                                      Exists
                                    </StatusIndicator>
                                    {t.variablesMatch ? (
                                      <StatusIndicator theme={theme} dotColor="#22c55e" color={theme.colors.muted} style={{ marginTop: 2 }}>
                                        Variables match
                                      </StatusIndicator>
                                    ) : (
                                      <>
                                        <StatusIndicator theme={theme} dotColor="#f59e0b" color="#f59e0b" style={{ marginTop: 2 }}>
                                          Variable mismatch
                                        </StatusIndicator>
                                        {t.variableMatchDetail?.missingInSes?.length > 0 && (
                                          <VariableList theme={theme}>
                                            Missing: {t.variableMatchDetail.missingInSes.join(', ')}
                                          </VariableList>
                                        )}
                                        {t.variableMatchDetail?.extraInSes?.length > 0 && (
                                          <VariableList theme={theme}>
                                            Extra: {t.variableMatchDetail.extraInSes.join(', ')}
                                          </VariableList>
                                        )}
                                      </>
                                    )}
                                  </>
                                ) : (
                                  <StatusIndicator theme={theme} dotColor="#ef4444" color="#ef4444">
                                    Not created
                                  </StatusIndicator>
                                )}
                              </Td>
                              <Td theme={theme}>
                                <ActionButtons theme={theme}>
                                  {isSuperAdmin() && (
                                    <SmallButton theme={theme} onClick={() => openEditModal(t)}>
                                      Configure
                                    </SmallButton>
                                  )}
                                  <SmallButton
                                    theme={theme}
                                    onClick={() => navigate(`/email-templates/designer/${t.id}`)}
                                    title="Open HTML designer"
                                  >
                                    Designer
                                  </SmallButton>
                                  {isSuperAdmin() && !t.sesTemplateExists && (
                                    <SmallButton
                                      theme={theme}
                                      variant="success"
                                      disabled={isCreating}
                                      onClick={() => handleCreateSesTemplate(t)}
                                      title="Create a default SES template for this event"
                                    >
                                      {isCreating ? 'Creating...' : 'Create in SES'}
                                    </SmallButton>
                                  )}
                                  {t.sesTemplateExists && (
                                    <SmallButton
                                      theme={theme}
                                      onClick={() => handlePreview(t)}
                                      disabled={previewLoading}
                                      title="Preview template HTML from SES"
                                    >
                                      Preview
                                    </SmallButton>
                                  )}
                                  <SmallButton
                                    theme={theme}
                                    variant="primary"
                                    disabled={!t.sesTemplateExists}
                                    onClick={() => openSendTestModal(t)}
                                    title={!t.sesTemplateExists ? 'SES template required' : 'Send test email'}
                                  >
                                    Send Test
                                  </SmallButton>
                                </ActionButtons>
                              </Td>
                            </Tr>
                          );
                        })}
                      </Tbody>
                    </Table>
                  </TableContainer>
                </CategorySection>
              ))
            )}
          </>
        )}

        {activeTab === 'logs' && (
          <TableContainer theme={theme}>
            {logsLoading ? (
              <LoadingState theme={theme}>Loading logs...</LoadingState>
            ) : logs.length === 0 ? (
              <EmptyState theme={theme}>No email logs yet.</EmptyState>
            ) : (
              <>
                <Table>
                  <Thead theme={theme}>
                    <tr>
                      <Th theme={theme}>Date</Th>
                      <Th theme={theme}>Event</Th>
                      <Th theme={theme}>Recipient</Th>
                      <Th theme={theme}>Status</Th>
                      <Th theme={theme}>Error</Th>
                    </tr>
                  </Thead>
                  <Tbody>
                    {logs.map(log => (
                      <Tr key={log.id} theme={theme}>
                        <Td theme={theme} style={{ whiteSpace: 'nowrap' }}>
                          {new Date(log.sentAt).toLocaleString()}
                        </Td>
                        <Td theme={theme}>
                          <Badge theme={theme} bg={`${theme.colors.tint}20`} color={theme.colors.tint}>
                            {log.eventType}
                          </Badge>
                        </Td>
                        <Td theme={theme}>
                          <div>{log.recipientName}</div>
                          <div style={{ fontSize: 12, color: theme.colors.muted }}>{log.recipientEmail}</div>
                        </Td>
                        <Td theme={theme}>
                          <LogStatusBadge status={log.status}>{log.status}</LogStatusBadge>
                        </Td>
                        <Td theme={theme}>
                          <span style={{ fontSize: 12, color: theme.colors.muted }}>
                            {log.errorMessage || '-'}
                          </span>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
                <PaginationRow theme={theme}>
                  <span>
                    Showing page {logPage} of {logTotalPages} ({logTotalCount} total)
                  </span>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <SmallButton
                      theme={theme}
                      disabled={logPage <= 1}
                      onClick={() => setLogPage(p => p - 1)}
                    >
                      Previous
                    </SmallButton>
                    <SmallButton
                      theme={theme}
                      disabled={logPage >= logTotalPages}
                      onClick={() => setLogPage(p => p + 1)}
                    >
                      Next
                    </SmallButton>
                  </div>
                </PaginationRow>
              </>
            )}
          </TableContainer>
        )}

        {/* Branding Tab */}
        {activeTab === 'branding' && (
          <>
            {brandingLoading ? (
              <LoadingState theme={theme}>Loading branding config...</LoadingState>
            ) : (
              <>
                <BrandingGrid theme={theme}>
                  <BrandingCard theme={theme}>
                    <BrandingCardTitle theme={theme}>Company Info</BrandingCardTitle>
                    <FormGroup theme={theme}>
                      <Label theme={theme}>Company Name</Label>
                      <TextInput
                        theme={theme}
                        value={brandingForm.companyName}
                        onChange={e => setBrandingForm(prev => ({ ...prev, companyName: e.target.value }))}
                      />
                    </FormGroup>
                    <FormGroup theme={theme}>
                      <Label theme={theme}>Logo URL</Label>
                      <LogoRow theme={theme}>
                        <TextInput
                          theme={theme}
                          value={brandingForm.logoUrl}
                          onChange={e => setBrandingForm(prev => ({ ...prev, logoUrl: e.target.value }))}
                          placeholder="https://example.com/logo.png"
                          style={{ flex: 1 }}
                        />
                        <SmallButton theme={theme} variant="primary" onClick={() => setLogoPickerOpen(true)}>
                          Browse Assets
                        </SmallButton>
                      </LogoRow>
                      {brandingForm.logoUrl && (
                        <div style={{ marginTop: 8 }}>
                          <LogoPreview
                            theme={theme}
                            src={brandingForm.logoUrl}
                            alt="Logo preview"
                            onError={e => { e.target.style.display = 'none'; }}
                          />
                        </div>
                      )}
                    </FormGroup>
                  </BrandingCard>

                  <BrandingCard theme={theme}>
                    <BrandingCardTitle theme={theme}>Colors</BrandingCardTitle>
                    <FormGroup theme={theme}>
                      <Label theme={theme}>Primary Color</Label>
                      <ColorRow>
                        <ColorSwatch
                          theme={theme}
                          type="color"
                          value={brandingForm.primaryColor}
                          onChange={e => setBrandingForm(prev => ({ ...prev, primaryColor: e.target.value }))}
                        />
                        <TextInput
                          theme={theme}
                          value={brandingForm.primaryColor}
                          onChange={e => setBrandingForm(prev => ({ ...prev, primaryColor: e.target.value }))}
                          style={{ flex: 1 }}
                          maxLength={7}
                        />
                      </ColorRow>
                    </FormGroup>
                    <FormGroup theme={theme}>
                      <Label theme={theme}>Secondary Color</Label>
                      <ColorRow>
                        <ColorSwatch
                          theme={theme}
                          type="color"
                          value={brandingForm.secondaryColor || '#64748b'}
                          onChange={e => setBrandingForm(prev => ({ ...prev, secondaryColor: e.target.value }))}
                        />
                        <TextInput
                          theme={theme}
                          value={brandingForm.secondaryColor}
                          onChange={e => setBrandingForm(prev => ({ ...prev, secondaryColor: e.target.value }))}
                          style={{ flex: 1 }}
                          placeholder="#64748b"
                          maxLength={7}
                        />
                      </ColorRow>
                    </FormGroup>
                  </BrandingCard>
                </BrandingGrid>

                <BrandingGrid theme={theme} style={{ marginTop: theme.spacing.lg }}>
                  <BrandingCard theme={theme} style={{ gridColumn: '1 / -1' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: theme.spacing.md }}>
                      <BrandingCardTitle theme={theme} style={{ margin: 0 }}>Brand Style & Voice</BrandingCardTitle>
                      <SmallButton
                        theme={theme}
                        variant="primary"
                        disabled={generatingRefHtml}
                        onClick={async () => {
                          setGeneratingRefHtml(true);
                          try {
                            const result = await emailTemplateService.generateReferenceHtml({
                              companyName: brandingForm.companyName,
                              logoUrl: brandingForm.logoUrl || null,
                              primaryColor: brandingForm.primaryColor,
                              secondaryColor: brandingForm.secondaryColor || null,
                              styleDescription: brandingForm.styleDescription || null,
                            });
                            const data = result?.data || result;
                            if (data.success && data.htmlContent) {
                              setBrandingForm(prev => ({ ...prev, referenceTemplateHtml: data.htmlContent }));
                              toast.success('Reference template generated');
                            } else {
                              toast.error(data.errorMessage || 'Failed to generate reference template');
                            }
                          } catch (err) {
                            toast.error(err?.response?.data?.message || 'Failed to generate reference template');
                          } finally {
                            setGeneratingRefHtml(false);
                          }
                        }}
                      >
                        {generatingRefHtml ? 'Generating...' : 'Generate Reference HTML'}
                      </SmallButton>
                    </div>
                    <DescriptionText theme={theme} style={{ marginBottom: 8 }}>
                      Describe your brand's tone, style, and voice. This is used by AI template generation to ensure consistent messaging.
                    </DescriptionText>
                    <TextArea
                      theme={theme}
                      value={brandingForm.styleDescription}
                      onChange={e => setBrandingForm(prev => ({ ...prev, styleDescription: e.target.value }))}
                      placeholder="e.g., Professional but friendly. Use clear, concise language. Avoid jargon. Address customers by first name..."
                      style={{ minHeight: 80 }}
                      maxLength={2000}
                    />
                  </BrandingCard>
                </BrandingGrid>

                <div style={{ marginTop: theme.spacing.lg }}>
                  <BrandingCardTitle theme={theme}>Reference Template HTML</BrandingCardTitle>
                  <DescriptionText theme={theme} style={{ marginBottom: 8 }}>
                    This template is used as a reference by the AI generator for consistent branding.
                  </DescriptionText>
                  <BrandingGrid theme={theme}>
                    <div>
                      <Label theme={theme}>HTML Editor</Label>
                      <RefTextArea
                        theme={theme}
                        value={brandingForm.referenceTemplateHtml}
                        onChange={e => setBrandingForm(prev => ({ ...prev, referenceTemplateHtml: e.target.value }))}
                        placeholder="<html>&#10;  <body>&#10;    <!-- Your reference template HTML -->&#10;  </body>&#10;</html>"
                        spellCheck={false}
                      />
                    </div>
                    <div>
                      <Label theme={theme}>Preview</Label>
                      <RefPreviewFrame
                        theme={theme}
                        srcDoc={brandingForm.referenceTemplateHtml}
                        sandbox="allow-same-origin"
                        title="Reference Template Preview"
                      />
                    </div>
                  </BrandingGrid>
                </div>

                <div style={{ marginTop: theme.spacing.lg, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                  {isSuperAdmin() && (
                    <div style={{ maxWidth: 420 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: theme.colors.text, marginBottom: 4 }}>
                        Batch Regenerate All Templates
                      </div>
                      <div style={{ fontSize: 12, color: theme.colors.muted, marginBottom: 8 }}>
                        Regenerate all email templates to apply your latest branding changes.
                      </div>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={openBatchModal}
                        disabled={templates.length === 0}
                      >
                        Batch Regenerate
                      </Button>
                    </div>
                  )}
                  <Button size="sm" onClick={handleBrandingSave} disabled={brandingSaving}>
                    {brandingSaving ? 'Saving...' : 'Save Branding'}
                  </Button>
                </div>
              </>
            )}
          </>
        )}

        {/* Assets Tab */}
        {activeTab === 'assets' && (
          <>
            <UploadArea theme={theme}>
              <HiddenInput
                type="file"
                id="asset-tab-upload"
                accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
                onChange={handleAssetUpload}
              />
              <Button
                size="sm"
                onClick={() => document.getElementById('asset-tab-upload').click()}
                disabled={assetUploading}
              >
                {assetUploading ? 'Uploading...' : 'Upload Image'}
              </Button>
              {assetUploading && (
                <>
                  <span style={{ fontSize: 12, color: theme.colors.muted }}>{assetUploadProgress}%</span>
                  <ProgressBarSmall theme={theme}>
                    <ProgressFillSmall theme={theme} $progress={assetUploadProgress} />
                  </ProgressBarSmall>
                </>
              )}
            </UploadArea>

            {assetsLoading ? (
              <LoadingState theme={theme}>Loading assets...</LoadingState>
            ) : assets.length === 0 ? (
              <EmptyState theme={theme}>
                No email assets uploaded yet. Upload images to use in your email templates.
              </EmptyState>
            ) : (
              <AssetGrid theme={theme}>
                {assets.map(asset => (
                  <AssetCard key={asset.id} theme={theme}>
                    <AssetThumb theme={theme}>
                      <img src={asset.url} alt={asset.altText || asset.fileName} loading="lazy" />
                    </AssetThumb>
                    <AssetCardBody theme={theme}>
                      <AssetFileName theme={theme}>{asset.fileName}</AssetFileName>
                      <AssetMeta theme={theme}>
                        {formatFileSize(asset.fileSize)} &middot; {asset.contentType.split('/')[1]?.toUpperCase()}
                      </AssetMeta>
                      <AssetActions theme={theme}>
                        <SmallButton theme={theme} variant="primary" onClick={() => copyAssetUrl(asset.url)}>
                          Copy URL
                        </SmallButton>
                        {deleteConfirm === asset.id ? (
                          <>
                            <SmallButton
                              theme={theme}
                              style={{ color: '#ef4444', borderColor: '#ef4444' }}
                              onClick={() => handleAssetDelete(asset.id)}
                            >
                              Confirm
                            </SmallButton>
                            <SmallButton theme={theme} onClick={() => setDeleteConfirm(null)}>
                              Cancel
                            </SmallButton>
                          </>
                        ) : (
                          <SmallButton theme={theme} onClick={() => setDeleteConfirm(asset.id)}>
                            Delete
                          </SmallButton>
                        )}
                      </AssetActions>
                    </AssetCardBody>
                  </AssetCard>
                ))}
              </AssetGrid>
            )}
          </>
        )}

        {/* Send Test Modal */}
        <Overlay isOpen={!!sendTestModal} theme={theme} onClick={() => setSendTestModal(null)}>
          <ModalContent theme={theme} onClick={e => e.stopPropagation()}>
            <ModalHeader theme={theme}>
              <ThemedText type="title" style={{ fontSize: '18px' }}>
                Send Test: {sendTestModal?.name}
              </ThemedText>
              <SmallButton theme={theme} onClick={() => setSendTestModal(null)}>X</SmallButton>
            </ModalHeader>

            {sendTestLoading ? (
              <LoadingState theme={theme}>Loading template variables...</LoadingState>
            ) : (
              <>
                <FormGroup theme={theme}>
                  <Label theme={theme}>Recipient Email</Label>
                  <TextInput
                    theme={theme}
                    type="email"
                    value={sendTestForm.email}
                    onChange={e => setSendTestForm(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="test@example.com"
                  />
                </FormGroup>

                <FormGroup theme={theme}>
                  <Label theme={theme}>Recipient Name</Label>
                  <TextInput
                    theme={theme}
                    value={sendTestForm.name}
                    onChange={e => setSendTestForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Test User"
                  />
                </FormGroup>

                {Object.keys(sendTestForm.params).length > 0 && (
                  <FormGroup theme={theme}>
                    <Label theme={theme}>Template Variables</Label>
                    <DescriptionText theme={theme}>
                      Edit the values that will be injected into the template.
                    </DescriptionText>
                    {Object.entries(sendTestForm.params).map(([key, value]) => (
                      <div key={key} style={{ marginTop: 6 }}>
                        <Label theme={theme} style={{ fontSize: 11, color: theme.colors.muted, fontFamily: 'monospace' }}>
                          {'{{' + key + '}}'}
                        </Label>
                        <TextInput
                          theme={theme}
                          value={value}
                          onChange={e => setSendTestForm(prev => ({
                            ...prev,
                            params: { ...prev.params, [key]: e.target.value },
                          }))}
                        />
                      </div>
                    ))}
                  </FormGroup>
                )}

                <ModalActions theme={theme}>
                  <Button variant="secondary" size="sm" onClick={() => setSendTestModal(null)}>
                    Cancel
                  </Button>
                  <Button size="sm" onClick={handleSendTest} disabled={sendingTest || !sendTestForm.email}>
                    {sendingTest ? 'Sending...' : 'Send Test Email'}
                  </Button>
                </ModalActions>
              </>
            )}
          </ModalContent>
        </Overlay>

        {/* Configure Modal */}
        <Overlay isOpen={!!editModal} theme={theme} onClick={() => setEditModal(null)}>
          <ModalContent theme={theme} onClick={e => e.stopPropagation()}>
            <ModalHeader theme={theme}>
              <ThemedText type="title" style={{ fontSize: '18px' }}>
                Configure: {editModal?.name}
              </ThemedText>
              <SmallButton theme={theme} onClick={() => setEditModal(null)}>X</SmallButton>
            </ModalHeader>

            {editModal?.sesTemplateName && (
              <FormGroup theme={theme}>
                <Label theme={theme}>SES Template Name</Label>
                <DescriptionText theme={theme} style={{ fontFamily: 'monospace', fontSize: 13, marginTop: 0 }}>
                  {editModal.sesTemplateName}
                </DescriptionText>
                <DescriptionText theme={theme}>
                  Auto-derived from event type and environment. Managed automatically.
                </DescriptionText>
              </FormGroup>
            )}

            <FormGroup theme={theme}>
              <Label theme={theme}>Sender Name (optional override)</Label>
              <TextInput
                theme={theme}
                value={editForm.senderName}
                onChange={e => setEditForm(prev => ({ ...prev, senderName: e.target.value }))}
                placeholder="aQRate"
              />
            </FormGroup>

            <FormGroup theme={theme}>
              <Label theme={theme}>Sender Email (optional override)</Label>
              <TextInput
                theme={theme}
                type="email"
                value={editForm.senderEmail}
                onChange={e => setEditForm(prev => ({ ...prev, senderEmail: e.target.value }))}
                placeholder="noreply@aqrate.ca"
              />
            </FormGroup>

            <ModalActions theme={theme}>
              <Button variant="secondary" size="sm" onClick={() => setEditModal(null)}>
                Cancel
              </Button>
              <Button size="sm" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </ModalActions>
          </ModalContent>
        </Overlay>

        {/* Preview Modal */}
        <Overlay isOpen={!!previewModal} theme={theme} onClick={() => setPreviewModal(null)}>
          <ModalContent theme={theme} wide onClick={e => e.stopPropagation()}>
            <ModalHeader theme={theme}>
              <ThemedText type="title" style={{ fontSize: '18px' }}>
                Preview: {previewModal?.name}
              </ThemedText>
              <SmallButton theme={theme} onClick={() => setPreviewModal(null)}>X</SmallButton>
            </ModalHeader>
            {previewModal?.html && (
              <PreviewFrame
                theme={theme}
                srcDoc={previewModal.html}
                sandbox="allow-same-origin"
                title="Template Preview"
              />
            )}
          </ModalContent>
        </Overlay>
        {/* Batch Regenerate Modal */}
        <Overlay isOpen={batchModal} theme={theme} onClick={closeBatchModal}>
          <ModalContent theme={theme} wide onClick={e => e.stopPropagation()} style={{ maxWidth: 640, height: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <ModalHeader theme={theme}>
              <ThemedText type="title" style={{ fontSize: '18px' }}>
                Batch Regenerate Email Templates
              </ThemedText>
              <SmallButton theme={theme} onClick={closeBatchModal}>X</SmallButton>
            </ModalHeader>

            {batchStep === 'configure' && (
              <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
                {/* Scrollable body */}
                <div style={{ flex: 1, overflowY: 'auto', paddingRight: 4 }}>
                  <FormGroup theme={theme}>
                    <Label theme={theme} style={{ marginBottom: 8 }}>Regeneration Mode</Label>
                    <ModeCardGrid theme={theme}>
                      <ModeCard
                        theme={theme}
                        $selected={batchMode === 'Redesign'}
                        onClick={() => setBatchMode('Redesign')}
                      >
                        <input type="radio" name="batchMode" value="Redesign" checked={batchMode === 'Redesign'} onChange={() => setBatchMode('Redesign')} style={{ display: 'none' }} />
                        <ModeCardTitle theme={theme}><span>✦</span> Redesign</ModeCardTitle>
                        <ModeCardDesc theme={theme}>
                          Update styling and branding while preserving existing content and variables. Recommended after changing colors or reference HTML.
                        </ModeCardDesc>
                      </ModeCard>
                      <ModeCard
                        theme={theme}
                        $selected={batchMode === 'Wipe'}
                        onClick={() => setBatchMode('Wipe')}
                      >
                        <input type="radio" name="batchMode" value="Wipe" checked={batchMode === 'Wipe'} onChange={() => setBatchMode('Wipe')} style={{ display: 'none' }} />
                        <ModeCardTitle theme={theme}><span>↺</span> Wipe &amp; Regenerate</ModeCardTitle>
                        <ModeCardDesc theme={theme}>
                          Discard existing templates and generate fresh from current branding.
                        </ModeCardDesc>
                      </ModeCard>
                    </ModeCardGrid>
                  </FormGroup>

                  {/* LLM tier + parallel row */}
                  <div style={{ display: 'flex', gap: theme.spacing.md, marginBottom: theme.spacing.md, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                    <FormGroup theme={theme} style={{ marginBottom: 0, flex: 1, minWidth: 180 }}>
                      <Label theme={theme}>AI Model</Label>
                      <select
                        value={batchTier}
                        onChange={e => setBatchTier(Number(e.target.value))}
                        style={{
                          width: '100%', padding: '8px 10px',
                          border: `1px solid ${theme.colors.border}`,
                          borderRadius: theme.borderRadius.sm,
                          background: theme.colors.backgroundSecondary,
                          color: theme.colors.text,
                          fontSize: 13,
                        }}
                      >
                        <option value={1}>Fast — Claude Haiku (quick, lower cost)</option>
                        <option value={2}>Balanced — Claude Sonnet (recommended)</option>
                        <option value={3}>Powerful — Claude Opus (highest quality)</option>
                      </select>
                    </FormGroup>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: theme.colors.text, cursor: 'pointer', paddingBottom: 2, whiteSpace: 'nowrap' }}>
                      <input
                        type="checkbox"
                        checked={batchParallel}
                        onChange={e => setBatchParallel(e.target.checked)}
                        style={{ accentColor: theme.colors.tint, width: 15, height: 15 }}
                      />
                      Process all at the same time
                    </label>
                  </div>

                  <FormGroup theme={theme} style={{ marginBottom: 0 }}>
                    <TemplateSectionHeader theme={theme}>
                      <Label theme={theme} style={{ marginBottom: 0 }}>
                        Templates ({batchSelected?.size ?? 0} selected)
                      </Label>
                      <SelectAllLink
                        theme={theme}
                        onClick={() => {
                          if (batchSelected?.size === templates.length) {
                            setBatchSelected(new Set());
                          } else {
                            setBatchSelected(new Set(templates.map(t => t.id)));
                          }
                        }}
                      >
                        {batchSelected?.size === templates.length ? 'Deselect All' : 'Select All'}
                      </SelectAllLink>
                    </TemplateSectionHeader>
                    <div>
                      {allCategories.map(category => (
                        <div key={category}>
                          <TemplateCategoryLabel theme={theme}>{category}</TemplateCategoryLabel>
                          {grouped[category].map(t => (
                            <TemplateCheckRow
                              key={t.id}
                              theme={theme}
                              onClick={() => {
                                setBatchSelected(prev => {
                                  const next = new Set(prev);
                                  if (next.has(t.id)) next.delete(t.id);
                                  else next.add(t.id);
                                  return next;
                                });
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={batchSelected?.has(t.id) ?? false}
                                onChange={() => {}}
                                style={{ accentColor: theme.colors.tint }}
                              />
                              {t.name}
                              <Badge theme={theme} bg={`${theme.colors.tint}15`} color={theme.colors.tint} style={{ marginLeft: 'auto' }}>
                                {t.eventType}
                              </Badge>
                            </TemplateCheckRow>
                          ))}
                        </div>
                      ))}
                    </div>
                  </FormGroup>
                </div>

                {/* Fixed footer */}
                <ModalActions theme={theme} style={{ marginTop: theme.spacing.md, paddingTop: theme.spacing.md, borderTop: `1px solid ${theme.colors.border}` }}>
                  <Button variant="secondary" size="sm" onClick={closeBatchModal}>Cancel</Button>
                  <Button size="sm" onClick={handleBatchStart} disabled={(batchSelected?.size ?? 0) === 0}>
                    Start ({batchSelected?.size ?? 0} selected)
                  </Button>
                </ModalActions>
              </div>
            )}

            {batchStep === 'progress' && (
              <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
                <div style={{ fontSize: 13, color: theme.colors.muted, marginBottom: 10 }}>
                  {Object.values(batchProgress).filter(p => p.status === 'complete' || p.status === 'error').length}
                  {' / '}
                  {batchSelected?.size ?? 0} complete
                  {batchParallel && <span style={{ marginLeft: 8, color: theme.colors.tint }}>(parallel)</span>}
                </div>
                <div style={{ flex: 1, overflowY: 'auto' }}>
                  {Array.from(batchSelected ?? []).map(templateId => {
                    const template = templates.find(t => t.id === templateId);
                    if (!template) return null;
                    const prog = batchProgress[templateId];
                    const status = prog?.status ?? 'pending';
                    const statusLabel =
                      status === 'complete' ? '✓' :
                      status === 'error' ? '✗' :
                      status === 'processing' ? '⟳' : '…';
                    return (
                      <div key={templateId}>
                        <ProgressRow theme={theme}>
                          <ProgressRowName theme={theme}>{template.name}</ProgressRowName>
                          <ProgressRowCategory theme={theme}>{template.category}</ProgressRowCategory>
                          <ProgressRowStatus theme={theme} $status={status}>
                            {status === 'processing' ? (
                              <Spinner>⟳</Spinner>
                            ) : statusLabel}
                            {prog?.tokens ? ` (${prog.tokens}t)` : ''}
                          </ProgressRowStatus>
                        </ProgressRow>
                        {status === 'error' && prog?.error && (
                          <div style={{ fontSize: 11, color: '#ef4444', padding: '0 8px 6px 8px', marginTop: -4 }}>
                            {prog.error}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <ModalActions theme={theme} style={{ paddingTop: theme.spacing.md, borderTop: `1px solid ${theme.colors.border}` }}>
                  <Button variant="secondary" size="sm" onClick={closeBatchModal}>
                    Cancel
                  </Button>
                </ModalActions>
              </div>
            )}

            {batchStep === 'summary' && batchSummary && (
              <SummaryBox theme={theme} style={{ flex: 1, overflowY: 'auto' }}>
                <SummaryTitle theme={theme}>
                  {batchSummary.cancelled ? 'Cancelled' : 'Batch Complete'}
                </SummaryTitle>
                {batchSummary.succeeded > 0 && (
                  <SummaryLine theme={theme}>
                    ✓ {batchSummary.succeeded} template{batchSummary.succeeded !== 1 ? 's' : ''} updated successfully
                  </SummaryLine>
                )}
                {batchSummary.failed > 0 && (
                  <SummaryLine theme={theme} $error>
                    ✗ {batchSummary.failed} failed
                  </SummaryLine>
                )}
                {batchSummary.cancelled && (
                  <div style={{ fontSize: 12, color: theme.colors.muted, marginTop: 6 }}>
                    Templates that completed before cancellation have been updated.
                  </div>
                )}
                {batchSummary.failed > 0 && (
                  <ErrorList theme={theme}>
                    {Object.entries(batchProgress)
                      .filter(([, p]) => p.status === 'error')
                      .map(([templateId, p]) => {
                        const tmpl = templates.find(t => t.id === parseInt(templateId));
                        return (
                          <div key={templateId} style={{ padding: '4px 0', borderBottom: `1px solid ${theme.colors.border}` }}>
                            <strong>{tmpl?.name ?? templateId}</strong>: {p.error || 'Unknown error'}
                          </div>
                        );
                      })}
                  </ErrorList>
                )}
                <div style={{ marginTop: 24 }}>
                  <Button size="sm" onClick={closeBatchModal}>
                    Close
                  </Button>
                </div>
              </SummaryBox>
            )}
          </ModalContent>
        </Overlay>

        {/* Logo Asset Picker */}
        <EmailAssetPickerModal
          isOpen={logoPickerOpen}
          onClose={() => setLogoPickerOpen(false)}
          onSelect={(url) => {
            setBrandingForm(prev => ({ ...prev, logoUrl: url }));
          }}
        />
      </PageContainer>
    </Layout>
  );
};
